import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getData, postData } from '../Services/api';
import { toast } from 'react-toastify';

const POTypeMapping = () => {
    const [activeTab, setActiveTab] = useState('salesperson'); // 'salesperson' | 'accounts'
    const [allPOTypes, setAllPOTypes] = useState([]);
    const [userMappings, setUserMappings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [savingUserId, setSavingUserId] = useState(null);
    const [selectedPOTypes, setSelectedPOTypes] = useState({}); // userId -> array of po_type_ids

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch all company PO types (all=true returns all company types)
            const typesRes = await getData('/po-types?all=true');
            if (typesRes.status) {
                setAllPOTypes(typesRes.data || []);
            }

            // Fetch user mappings
            const mapRes = await getData('/po-types/user-mappings');
            if (mapRes.status) {
                const users = mapRes.data || [];
                setUserMappings(users);

                // Populate initial selection state
                const initialSelected = {};
                users.forEach(u => {
                    initialSelected[u.id] = (u.poTypes || []).map(pt => pt.id);
                });
                setSelectedPOTypes(initialSelected);
            }
        } catch (error) {
            console.error('Error loading PO Type mappings:', error);
            toast.error('Failed to load mapping data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleCheckboxToggle = (userId, poTypeId) => {
        setSelectedPOTypes(prev => {
            const currentList = prev[userId] || [];
            const exists = currentList.includes(poTypeId);
            const updatedList = exists
                ? currentList.filter(id => id !== poTypeId)
                : [...currentList, poTypeId];

            return {
                ...prev,
                [userId]: updatedList
            };
        });
    };

    const handleSaveUserMapping = async (userId) => {
        setSavingUserId(userId);
        try {
            const po_type_ids = selectedPOTypes[userId] || [];
            const res = await postData('/po-types/assign-user', {
                user_id: userId,
                po_type_ids
            });

            if (res.status) {
                toast.success(res.message || 'PO Types allocated successfully');
            } else {
                toast.error(res.message || 'Failed to allocate PO Types');
            }
        } catch (error) {
            console.error('Error saving mapping:', error);
            toast.error('Failed to save allocation');
        } finally {
            setSavingUserId(null);
        }
    };

    const filteredUsers = userMappings.filter(u => u.role === activeTab);

    return (
        <div className="pc-container">
            <div className="pc-content">
                <div className="page-header">
                    <div className="page-block">
                        <div className="row align-items-center">
                            <div className="col-md-12">
                                <ul className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/">Home</Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <Link to="/po-types/po-type-list">PO Types</Link>
                                    </li>
                                    <li className="breadcrumb-item" aria-current="page">
                                        PO Type Mapping
                                    </li>
                                </ul>
                            </div>
                            <div className="col-md-12">
                                <div className="page-header-title d-flex justify-content-between align-items-center">
                                    <h2 className="mb-0 fw-bold">User PO Type Allocation Mapping</h2>
                                    <Link to="/po-types/po-type-list" className="btn btn-outline-secondary btn-sm rounded-pill px-3">
                                        <i className="fas fa-arrow-left me-1"></i> Back to PO Types
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-sm-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-header bg-white border-bottom-0 pt-3 pb-0">
                                <div className="d-flex gap-2">
                                    <button
                                        type="button"
                                        className={`btn ${activeTab === 'salesperson' ? 'btn-primary shadow-sm' : 'btn-light text-secondary'} rounded-pill px-4 py-2 fw-bold me-2`}
                                        onClick={() => setActiveTab('salesperson')}
                                        style={{ transition: 'all 0.2s ease' }}
                                    >
                                        <i className="fas fa-user-tie me-2"></i> Salesperson PO Type Allocation
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn ${activeTab === 'accounts' ? 'btn-primary shadow-sm' : 'btn-light text-secondary'} rounded-pill px-4 py-2 fw-bold`}
                                        onClick={() => setActiveTab('accounts')}
                                        style={{ transition: 'all 0.2s ease' }}
                                    >
                                        <i className="fas fa-calculator me-2"></i> Accounts Page PO Type Mapping
                                    </button>
                                </div>
                            </div>
                            <div className="card-body pt-3">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>
                                    </div>
                                ) : filteredUsers.length === 0 ? (
                                    <div className="alert alert-info my-3 rounded-3">
                                        <i className="fas fa-info-circle me-2"></i> No {activeTab === 'salesperson' ? 'salesperson' : 'accounts'} users found in this company.
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle mb-0">
                                            <thead style={{ background: '#f8f9fa' }}>
                                                <tr className="text-secondary small fw-bold text-uppercase">
                                                    <th style={{ width: '50px' }}>#</th>
                                                    <th>User Details</th>
                                                    <th>Role</th>
                                                    <th>Allocated PO Types ({allPOTypes.length} Available)</th>
                                                    <th className="text-end">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredUsers.map((user, idx) => {
                                                    const userSelected = selectedPOTypes[user.id] || [];
                                                    return (
                                                        <tr key={user.id}>
                                                            <td className="fw-bold text-muted">{idx + 1}</td>
                                                            <td>
                                                                <div className="fw-bold text-dark">{user.username}</div>
                                                                <div className="text-muted small">{user.email} {user.mobile ? `| ${user.mobile}` : ''}</div>
                                                            </td>
                                                            <td>
                                                                <span className={`badge ${user.role === 'salesperson' ? 'bg-primary' : 'bg-info'} text-white px-3 py-2 rounded-pill font-weight-bold`}>
                                                                    {user.role === 'salesperson' ? 'Salesperson' : 'Accounts'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex flex-wrap gap-2 py-1">
                                                                    {allPOTypes.map(pt => {
                                                                        const isChecked = userSelected.includes(pt.id);
                                                                        return (
                                                                            <button
                                                                                key={pt.id}
                                                                                type="button"
                                                                                onClick={() => handleCheckboxToggle(user.id, pt.id)}
                                                                                className="btn btn-sm d-inline-flex align-items-center gap-2 rounded-pill px-3 py-1 border-0"
                                                                                style={{
                                                                                    background: isChecked ? '#4680ff' : '#f1f3f5',
                                                                                    color: isChecked ? '#ffffff' : '#495057',
                                                                                    fontWeight: isChecked ? '600' : '400',
                                                                                    boxShadow: isChecked ? '0 2px 6px rgba(70, 128, 255, 0.35)' : 'none',
                                                                                    transition: 'all 0.2s ease',
                                                                                    cursor: 'pointer'
                                                                                }}
                                                                            >
                                                                                <i className={`fas ${isChecked ? 'fa-check-circle text-white' : 'fa-circle text-muted'} fs-6`}></i>
                                                                                <span>{pt.type_name}</span>
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </td>
                                                            <td className="text-end">
                                                                <button
                                                                    className="btn btn-success btn-sm rounded-pill px-3 py-2 shadow-sm d-inline-flex align-items-center gap-1"
                                                                    disabled={savingUserId === user.id}
                                                                    onClick={() => handleSaveUserMapping(user.id)}
                                                                >
                                                                    {savingUserId === user.id ? (
                                                                        <>
                                                                            <i className="fas fa-spinner fa-spin"></i>
                                                                            <span>Saving...</span>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <i className="fas fa-save"></i>
                                                                            <span>Save Allocation</span>
                                                                        </>
                                                                    )}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

};

export default POTypeMapping;
